import { useCallback, useMemo, useState } from 'react'
import '@glideapps/glide-data-grid/dist/index.css'
import {
  DataEditor,
  type DrawCellCallback,
  type EditableGridCell,
  type GridCell,
  GridCellKind,
  type GridColumn,
  type GridMouseEventArgs,
  type GridSelection,
  type Item,
  type Rectangle,
  type Theme,
  emptyGridSelection
} from '@glideapps/glide-data-grid'
import type { ColumnSort } from '../HomePage/Modals/PoolCsvGridView.ts'
import { nextSort } from '../HomePage/Modals/PoolCsvGridView.ts'
import type { PrimerCell, PrimerGridColumn, PrimerGridRow } from '../../../Services/OrderWizard/primerGrid.ts'
import { PRIMER_GRID_COLUMNS, PRIMER_GRID_TITLES, isPrimerSide } from '../../../Services/OrderWizard/primerGrid.ts'

const ROW_HEIGHT = 34
const HEADER_HEIGHT = 36
const MAX_HEIGHT = 420

const WIDTHS: Record<PrimerGridColumn, number> = {
  sample: 190,
  file: 220,
  well: 64,
  run: 120,
  forward: 250,
  reverse: 250,
  source: 140,
  confidence: 96
}

const COLORS = {
  tagBg: '#e0f2fe',
  tagText: '#0c4a6e',
  tentative: '#b45309',
  mismatch: '#b91c1c',
  invalidBg: '#fee2e2',
  invalidText: '#991b1b',
  attentionRow: '#fffbeb',
  quiet: '#64748b'
}

/* the active colours of the type control on Samples & controls */
const TYPE_BADGE = {
  SAMPLE: { label: 'S', bg: '#e2e8f0', text: '#1e293b' },
  NEGATIVE_CONTROL: { label: 'NC', bg: '#e0f2fe', text: '#075985' }
} as const

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace'

const primerTone = (cell: PrimerCell): Partial<Theme> | undefined => {
  if (cell.invalid) {
    return { bgCell: COLORS.invalidBg, textDark: COLORS.invalidText }
  }
  if (cell.comparison.kind === 'tag-mismatch') {
    return { textDark: COLORS.mismatch }
  }
  if (cell.tentative) {
    return { textDark: COLORS.tentative }
  }
  return undefined
}

/** The item's name behind a read-only type badge, muted when taken from the file name; the type is changed on Samples & controls. */
const drawNameWithType = ({ ctx, rect, theme }: Parameters<DrawCellCallback>[0], row: PrimerGridRow) => {
  const badge = TYPE_BADGE[row.itemType]
  const x = rect.x + theme.cellHorizontalPadding
  const y = rect.y + rect.height / 2
  ctx.save()
  ctx.beginPath()
  ctx.rect(rect.x, rect.y, rect.width, rect.height)
  ctx.clip()
  ctx.textBaseline = 'middle'
  ctx.font = `600 10px ${theme.fontFamily}`
  const width = ctx.measureText(badge.label).width + 8
  ctx.fillStyle = badge.bg
  ctx.fillRect(x, y - 8, width, 16)
  ctx.fillStyle = badge.text
  ctx.fillText(badge.label, x + 4, y)
  ctx.font = `${theme.baseFontStyle} ${theme.fontFamily}`
  ctx.fillStyle = row.sampleSource === 'file' ? COLORS.quiet : theme.textDark
  ctx.fillText(row.sample, x + width + 6, y)
  ctx.restore()
}

export type GridHover = { row: PrimerGridRow; column: PrimerGridColumn; bounds: Rectangle }

/**
 * The primers as a sortable grid. Rows are a view over `rows` (`view` holds their indices, sorted), so
 * every grid coordinate is mapped back through it; only the primer columns edit, and a paste is handed
 * to the caller rather than applied.
 */
export const PrimerGrid = ({
  rows,
  view,
  sort,
  editable,
  onSort,
  onEdit,
  onPaste,
  onFocusChange,
  onRowsSelected,
  onHover
}: {
  rows: readonly PrimerGridRow[]
  view: readonly number[]
  sort: ColumnSort | null
  editable: boolean
  onSort: (sort: ColumnSort | null) => void
  onEdit: (row: PrimerGridRow, column: 'forward' | 'reverse', value: string) => void
  onPaste: (at: { row: number; column: PrimerGridColumn }, values: readonly (readonly string[])[]) => void
  onFocusChange: (at: { row: number; column: PrimerGridColumn } | undefined) => void
  /** The rows the selected range covers. */
  onRowsSelected?: (rows: PrimerGridRow[]) => void
  onHover: (hover: GridHover | undefined) => void
}) => {
  const [selection, setSelection] = useState<GridSelection>(emptyGridSelection)
  const [portal, setPortal] = useState<HTMLDivElement | null>(null)
  const portalRef = useMemo(() => (portal ? { current: portal } : undefined), [portal])

  const columns = useMemo<GridColumn[]>(
    () =>
      PRIMER_GRID_COLUMNS.map((column) => ({
        id: column,
        title: `${PRIMER_GRID_TITLES[column]}${sort?.column === column ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ''}`,
        width: WIDTHS[column]
      })),
    [sort]
  )

  const rowAt = useCallback((row: number): PrimerGridRow | undefined => rows[view[row]], [rows, view])

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const column = PRIMER_GRID_COLUMNS[col]
      const data = rowAt(row)
      if (!data || !column) {
        return { kind: GridCellKind.Text, data: '', displayData: '', allowOverlay: false, readonly: true }
      }
      if (isPrimerSide(column)) {
        const cell = data[column]
        return {
          kind: GridCellKind.Text,
          data: cell.value,
          displayData: cell.value,
          allowOverlay: editable,
          readonly: !editable,
          themeOverride: primerTone(cell)
        }
      }
      const text = data[column]
      const quiet = column === 'source' && data.sourceTone !== 'quiet' ? { textDark: COLORS.tentative } : undefined
      return { kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false, readonly: true, themeOverride: quiet }
    },
    [rowAt, editable]
  )

  /* the tag in front of the gene primer is drawn on its own background, as the step shows it elsewhere */
  const drawCell = useCallback<DrawCellCallback>(
    (args, drawContent) => {
      const column = PRIMER_GRID_COLUMNS[args.col]
      const data = rowAt(args.row)
      if (data && column === 'sample') {
        drawNameWithType(args, data)
        return
      }
      if (!data || !column || !isPrimerSide(column) || !data[column].value) {
        drawContent()
        return
      }
      const { value, tag } = data[column]
      const { ctx, rect, theme } = args
      const x = rect.x + theme.cellHorizontalPadding
      const y = rect.y + rect.height / 2
      ctx.save()
      ctx.beginPath()
      ctx.rect(rect.x, rect.y, rect.width, rect.height)
      ctx.clip()
      ctx.font = `${theme.baseFontStyle} ${MONO}`
      ctx.textBaseline = 'middle'
      const shownTag = value.startsWith(tag) ? tag : ''
      const tagWidth = shownTag ? ctx.measureText(shownTag).width : 0
      if (shownTag) {
        ctx.fillStyle = COLORS.tagBg
        ctx.fillRect(x - 1, y - 9, tagWidth + 2, 18)
        ctx.fillStyle = COLORS.tagText
        ctx.fillText(shownTag, x, y)
      }
      ctx.fillStyle = primerTone(data[column])?.textDark ?? theme.textDark
      ctx.fillText(value.slice(shownTag.length), x + tagWidth, y)
      ctx.restore()
    },
    [rowAt]
  )

  const getRowThemeOverride = useCallback(
    (row: number): Partial<Theme> | undefined => (rowAt(row)?.attention ? { bgCell: COLORS.attentionRow } : undefined),
    [rowAt]
  )

  const onCellEdited = useCallback(
    ([col, row]: Item, value: EditableGridCell) => {
      const column = PRIMER_GRID_COLUMNS[col]
      const data = rowAt(row)
      if (data && column && isPrimerSide(column) && value.kind === GridCellKind.Text) {
        onEdit(data, column, value.data ?? '')
      }
    },
    [rowAt, onEdit]
  )

  const getCellsForSelection = useCallback(
    (selected: Rectangle): GridCell[][] =>
      Array.from({ length: selected.height }, (_, dy) =>
        Array.from({ length: selected.width }, (_, dx) => getCellContent([selected.x + dx, selected.y + dy]))
      ),
    [getCellContent]
  )

  const onSelection = useCallback(
    (next: GridSelection) => {
      setSelection(next)
      const cell = next.current?.cell
      onFocusChange(cell ? { column: PRIMER_GRID_COLUMNS[cell[0]] ?? 'forward', row: cell[1] } : undefined)
      const range = next.current?.range
      onRowsSelected?.(
        range ? Array.from({ length: range.height }, (_, dy) => rowAt(range.y + dy)).filter((row): row is PrimerGridRow => !!row) : []
      )
    },
    [onFocusChange, onRowsSelected, rowAt]
  )

  const onItemHovered = useCallback(
    (args: GridMouseEventArgs) => {
      if (args.kind !== 'cell') {
        onHover(undefined)
        return
      }
      const [col, row] = args.location
      const data = rowAt(row)
      const column = PRIMER_GRID_COLUMNS[col]
      onHover(data && column ? { row: data, column, bounds: args.bounds } : undefined)
    },
    [rowAt, onHover]
  )

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200" data-testid="wizard-primer-grid">
      <DataEditor
        columns={columns}
        rows={view.length}
        getCellContent={getCellContent}
        drawCell={drawCell}
        getRowThemeOverride={getRowThemeOverride}
        onCellEdited={editable ? onCellEdited : undefined}
        getCellsForSelection={getCellsForSelection}
        onPaste={(target, values) => {
          if (editable) {
            onPaste({ column: PRIMER_GRID_COLUMNS[target[0]] ?? 'forward', row: target[1] }, values)
          }
          return false
        }}
        onHeaderClicked={(col) => {
          const column = PRIMER_GRID_COLUMNS[col]
          if (column) {
            onSort(nextSort(sort, column))
          }
        }}
        gridSelection={selection}
        onGridSelectionChange={onSelection}
        onItemHovered={onItemHovered}
        rowMarkers="number"
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        rangeSelect="rect"
        columnSelect="none"
        rowSelect="none"
        freezeColumns={1}
        smoothScrollX
        smoothScrollY
        width="100%"
        height={Math.min(MAX_HEIGHT, HEADER_HEIGHT + view.length * ROW_HEIGHT + 2)}
        portalElementRef={portalRef}
        keybindings={{ search: false, copy: true, paste: true, downFill: false }}
      />
      <div ref={setPortal} style={{ position: 'fixed', inset: 0, zIndex: 10_000, pointerEvents: 'none' }} />
    </div>
  )
}
